// MapSearch.js
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import axios from '../services/axios';
import './MapSearch.css';
import _ from 'lodash'; // Add this import at the top

// Fix marker icon paths in webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// For the filter dropdowns, we assume a list of cities is available.
import { initialCities } from './cities';

const PROPERTY_INFO_CODES = {
  House: ['.6.', '.D.', '.F.', '.G.', '.J.', '.K.', '.L.', '.M.', '.N.', '.R.', '.S.', '.X.'],
  Condo: ['.2.', '.3.', '.4.', '.C.', '.E.', '.H.', '.P.', '.W.', '.Z.'],
  Townhouse: ['.A.', '.T.'],
};

// Sample data (fallback if API fails)
const sampleListings = [];

// Helper: Format prices for marker icons in abbreviated format (e.g. "$1.33 M")
const formatMarkerPrice = (price) => {
  if (price >= 1000000) {
    return `$${(price / 1000000).toFixed(2)} M`;
  }
  return `$${Math.round(price / 1000)}K`;
};

// Helper: Format the exact price for popups (with full number and optional " / month")
const formatExactPrice = (price, isRental) => {
  let formatted = `$${Number(price).toLocaleString()}`;
  if (isRental) {
    formatted += ' / month';
  }
  return formatted;
};

const MapSearch = ({ onSelectListing }) => {
  // --- Map and Marker States ---
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const apiCallMadeRef = useRef(false);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Filter States ---
  const [filters, setFilters] = useState({
    city: 'All',
    minPrice: 0,
    maxPrice: 10000000,
    bedrooms: 'Any',
    bathrooms: 'Any',
    searchQuery: '',
    currentPage: 1
  });
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [uniqueCities] = useState(initialCities);
  const [filteredCities, setFilteredCities] = useState([]);
  const searchTimeoutRef = useRef(null);
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  const getClusterRadiusForZoom = (zoom) => {
    if (zoom >= 17) {
      return 20;
    } else if (zoom >= 15) {
      return 40;
    } else if (zoom >= 12) {
      return 60;
    } else {
      return 80;
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const [isPropertyTypeOpen, setIsPropertyTypeOpen] = useState(false);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]);
  const [tempPropertyTypes, setTempPropertyTypes] = useState([]);

  // Add this function to clear all filters
  const clearFilters = () => {
    // Reset all filter values
    handleFilterChange('city', 'All');
    handleFilterChange('minPrice', 0);
    handleFilterChange('maxPrice', 10000000);
    handleFilterChange('bedrooms', 'Any');
    handleFilterChange('bathrooms', 'Any');
    handleFilterChange('searchQuery', '');
    setManualSearchQuery('');
    setSelectedPropertyTypes([]);
    setTempPropertyTypes([]);
    
    // Update URL to remove all filters
    window.history.pushState(null, '', '/map');
  };

  // Add this function to remove filters
  const removeFilter = (filterType) => {
    switch (filterType) {
      case 'city':
        handleFilterChange('city', 'All');
        break;
      case 'price':
        handleFilterChange('minPrice', 0);
        handleFilterChange('maxPrice', 10000000);
        break;
      case 'bedrooms':
        handleFilterChange('bedrooms', 'Any');
        break;
      case 'bathrooms':
        handleFilterChange('bathrooms', 'Any');
        break;
      case 'search':
        setManualSearchQuery('');
        handleFilterChange('searchQuery', '');
        break;
      case 'propertyType':
        setSelectedPropertyTypes([]);
        setTempPropertyTypes([]);
        handleFilterChange('propertyTypes', []);
        break;
      default:
        break;
    }
  };

  // Add these functions to your component
  const handleSearch = () => {
    if (!manualSearchQuery.trim()) return;
    
    // Update searchQuery in filters
    handleFilterChange('searchQuery', manualSearchQuery.trim());
  };

  

  // --- Fix Leaflet Default Icon Issue ---
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: icon,
      iconUrl: icon,
      shadowUrl: iconShadow,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  }, []);

  // --- Initialize Map and Marker Cluster ---
  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = L.map('map', {
        center: [43.7, -79.4],
        zoom: 9,
        minZoom: 3,
        maxZoom: 18,
        zoomControl: false,
        preferCanvas: true,  // Use canvas renderer for better performance
        renderer: L.canvas()  // Explicitly set canvas renderer
      });
      

      
      L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
      
      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 19,
          crossOrigin: true,  // Enable CORS
          noWrap: true  // Prevent tiles from wrapping around the globe
        }
      ).addTo(mapRef.current);

      markerLayerRef.current = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: false,
        disableClusteringAtZoom: null,
        chunkedLoading: true,
        chunkInterval: 200, // Add this
        chunkDelay: 50, // Add this
        maxClusterRadius: 80,
        zoomToBoundsOnClick: true,
        removeOutsideVisibleBounds: true,
        animate: false,
        singleMarkerMode: false,
        
        // Add these performance optimizations
        clusterPane: 'markerPane',
        iconCreateFunction: (cluster) => {
          const childCount = cluster.getChildCount();
          let size = 'small';
          if (childCount > 50) {
            size = 'large';
          } else if (childCount > 10) {
            size = 'medium';  
          }
          
          return L.divIcon({
            html: `<div class="custom-cluster-marker ${size}">${childCount}</div>`,
            className: 'custom-cluster',
            iconSize: size === 'large' ? L.point(50, 50) : 
                     size === 'medium' ? L.point(40, 40) : 
                     L.point(30, 30)
          });
        }
      });
      mapRef.current.addLayer(markerLayerRef.current);

      // Around line 255, replace the existing clusterclick handler
markerLayerRef.current.on('clusterclick', function (e) {
  const cluster = e.layer;
  const children = cluster.getAllChildMarkers();
  
  if (children.length === 1) return;
  if (children.length > 10) return;
  
  e.originalEvent.preventDefault();
  
  const popup = L.popup({
    closeButton: true,
    className: 'custom-cluster-popup',
    offset: L.point(0, -20)
  }).setLatLng(cluster.getLatLng());

  const addedListingIds = new Set();
  let contentHTML = `<div class="cluster-popup-scrollable">`;
  
  children.forEach((child) => {
    const listing = child.listingData;
    
    if (addedListingIds.has(listing.ml_num)) {
      return;
    }
    
    addedListingIds.add(listing.ml_num);
    
    // Fix: Use first_photo instead of photo_urls
    const firstPhoto = listing.first_photo || '';
    
    contentHTML += `
      <div class="cluster-item">
        ${
          firstPhoto
            ? `<div class="cluster-item-image">
                 <img src="${firstPhoto}" alt="${listing.address}" />
               </div>`
            : ''
        }
        <div class="cluster-item-info">
          <h3>${listing.address}${
            listing.apt_num ? ` #${listing.apt_num}` : ''
          }</h3>
          <p>${listing.community || listing.municipality}</p>
          <p class="cluster-item-price">${formatExactPrice(listing.price, listing.rental)}</p>
          <p class="cluster-item-details">
            <span>🛏️ ${listing.bedrooms || 'N/A'}</span>
            <span>🚿 ${listing.bathrooms || 'N/A'}</span>
            <span>📏 ${listing.square_feet ? listing.square_feet + ' sq ft' : 'N/A'}</span>
          </p>
          <a href="/listing/${listing.ml_num}/${listing.address
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9\-]/g, '')}" class="popup-view-button">
            View Listing
          </a>
        </div>
      </div>
    `;
  });
  
  contentHTML += `</div>`;
  popup.setContent(contentHTML);
  popup.openOn(mapRef.current);
});
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Around line 210, after the cluster radius useEffect
useEffect(() => {
  if (!mapRef.current) return;
  
  const updateMarkers = _.debounce(async () => {
    // Check if map still exists
    if (!mapRef.current) return;
    
    const bounds = mapRef.current.getBounds();
    const zoom = mapRef.current.getZoom();
    
    if (zoom < 8) {
      return;
    }
    
    if (markerLayerRef.current) {
      markerLayerRef.current.options.removeOutsideVisibleBounds = true;
      const newRadius = getClusterRadiusForZoom(zoom);
      markerLayerRef.current.options.maxClusterRadius = newRadius;
    }
  }, 300);
  
  mapRef.current.on('moveend', updateMarkers);
  mapRef.current.on('zoomend', updateMarkers);
  
  return () => {
    // Add null check here
    if (mapRef.current) {
      mapRef.current.off('moveend', updateMarkers);
      mapRef.current.off('zoomend', updateMarkers);
    }
  };
}, []);

  

  // --- Helper function to add a marker to the map ---
  const addMarkerToMap = (listing) => {
    // Use the database latitude and longitude directly
    const lat = parseFloat(listing.latitude);
    const lng = parseFloat(listing.longitude);
    
    // Skip if coordinates are invalid
    if (isNaN(lat) || isNaN(lng)) return;
    
    // Create custom icon
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div class="marker-price">${formatMarkerPrice(listing.price)}</div>`,
      iconSize: [80, 30],
      iconAnchor: [40, 30]
    });
    
    // Create marker
    const marker = L.marker([lat, lng], { 
      icon: customIcon,
      bubblingMouseEvents: false,
      riseOnHover: true
    });
    
    // Add a unique identifier
    marker._leaflet_id = `marker_${listing.ml_num || Math.random().toString(36).substring(2, 10)}`;
    marker.listingData = listing;
    
    // Use the first photo directly from the query result
    const mainPhoto = listing.first_photo || '';
    
    // Create popup content
    const popupContent = `
      <div class="map-popup-content">
        ${
          mainPhoto
            ? `<div class="popup-image-container">
                 <img src="${mainPhoto}" alt="${listing.address}" class="popup-image">
               </div>`
            : ''
        }
        <div class="popup-header">
          <h3 class="popup-address">${listing.address}${
            listing.apt_num ? ` #${listing.apt_num}` : ''
          }</h3>
          <p class="popup-community">${listing.community || listing.municipality}</p>
        </div>
        <div class="popup-price">${formatExactPrice(listing.price, listing.rental)}</div>
        <div class="popup-details">
          <span class="popup-detail-item"><span class="popup-icon">🛏️</span> ${listing.bedrooms || 'N/A'}</span>
          <span class="popup-detail-item"><span class="popup-icon">🚿</span> ${listing.bathrooms || 'N/A'}</span>
          <span class="popup-detail-item"><span class="popup-icon">📏</span> ${
            listing.square_feet ? listing.square_feet + ' sq ft' : 'N/A'
          }</span>
        </div>
        <a href="/listing/${listing.ml_num}/${listing.address
          .replace(/\s+/g, '-')
          .replace(/[^a-zA-Z0-9\-]/g, '')}" class="popup-view-button">
          View Listing
        </a>
      </div>
    `;
    
    const popup = L.popup({
      closeButton: true,
      className: 'custom-popup',
      maxWidth: 320,
      minWidth: 300
    }).setContent(popupContent);
    
    marker.bindPopup(popup);
    
    marker.on('click', () => {
      if (onSelectListing) onSelectListing(listing);
    });
    
    markerLayerRef.current.addLayer(marker);
  };

  // --- Fetch Listings When Filters Change ---
useEffect(() => {
  if (!apiCallMadeRef.current) {
    apiCallMadeRef.current = true;
    setTimeout(() => fetchFilteredListings(), 500);
  } else {
    fetchFilteredListings();
  }
}, [filters, selectedPropertyTypes]);

const fetchFilteredListings = async () => {
  setLoading(true);
  try {
    const selectedCodes = selectedPropertyTypes
      .flatMap((type) => PROPERTY_INFO_CODES[type] || [])
      .join(',');
      
    const params = {
      limit: 10000,
      offset: (filters.currentPage - 1) * 100,
      city: filters.city && filters.city !== 'All' ? filters.city : null,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      bedrooms: filters.bedrooms !== 'Any' ? filters.bedrooms : null,
      bathrooms: filters.bathrooms !== 'Any' ? filters.bathrooms : null,
      search: filters.searchQuery ? filters.searchQuery : null,
      property_info: selectedCodes || null,
      status: 'A'  // Only get active listings
    };
    
    // Use the new optimized endpoint for map data
    const response = await axios.get('/listings/map', { params });
    if (response.data && response.data.length > 0) {
      updateMapWithListings(response.data);
    } else {
      updateMapWithListings(sampleListings);
    }
  } catch (error) {
    console.error('Error fetching listings:', error);
    updateMapWithListings(sampleListings);
  }
};

  // --- Update Map with Listings ---
  const updateMapWithListings = (listingsData) => {
    if (!mapRef.current || !markerLayerRef.current) return;
    
    console.log(`Processing ${listingsData.length} listings`);
    
    // Clear previous markers more efficiently
    markerLayerRef.current.clearLayers();
    
    // Use chunked loading for better performance
    let markerArray = [];
    listingsData.forEach(listing => {
      if (listing.latitude && listing.longitude && 
          !isNaN(parseFloat(listing.latitude)) && !isNaN(parseFloat(listing.longitude))) {
        const marker = createMarker(listing); // Extract marker creation to a separate function
        if (marker) markerArray.push(marker);
      }
    });
    
    // Add all markers at once
    markerLayerRef.current.addLayers(markerArray);
    
    // Don't fit bounds automatically - let user control the view
    if (markerLayerRef.current.getLayers().length > 0 && filters.currentPage === 1) {
      const bounds = markerLayerRef.current.getBounds();
      if (bounds && bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
    
    setListings(listingsData);
    setLoading(false);
  };
  
  // Extract marker creation to a separate function for optimization
  const createMarker = (listing) => {
    const lat = parseFloat(listing.latitude);
    const lng = parseFloat(listing.longitude);
    
    if (isNaN(lat) || isNaN(lng)) return null;
    
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div class="marker-price">${formatMarkerPrice(listing.price)}</div>`,
      iconSize: [80, 30],
      iconAnchor: [40, 30]
    });
    
    const marker = L.marker([lat, lng], { 
      icon: customIcon,
      bubblingMouseEvents: false,
      riseOnHover: true
    });
    
    marker._leaflet_id = `marker_${listing.ml_num || Math.random().toString(36).substring(2, 10)}`;
    marker.listingData = listing;
    
    // Create popup content only when needed (lazy loading)
    marker.on('click', () => {
      const popup = createPopupContent(listing);
      marker.bindPopup(popup).openPopup();
      if (onSelectListing) onSelectListing(listing);
    });
    
    return marker;
  };
  
  const createPopupContent = (listing) => {
    const mainPhoto = listing.first_photo || '';
    const popupContent = `
      <div class="map-popup-content">
        ${
          mainPhoto
            ? `<div class="popup-image-container">
                 <img src="${mainPhoto}" alt="${listing.address}" class="popup-image">
               </div>`
            : ''
        }
        <div class="popup-header">
          <h3 class="popup-address">${listing.address}${
            listing.apt_num ? ` #${listing.apt_num}` : ''
          }</h3>
          <p class="popup-community">${listing.community || listing.municipality}</p>
        </div>
        <div class="popup-price">${formatExactPrice(listing.price, listing.rental)}</div>
        <div class="popup-details">
          <span class="popup-detail-item"><span class="popup-icon">🛏️</span> ${listing.bedrooms || 'N/A'}</span>
          <span class="popup-detail-item"><span class="popup-icon">🚿</span> ${listing.bathrooms || 'N/A'}</span>
          <span class="popup-detail-item"><span class="popup-icon">📏</span> ${
            listing.square_feet ? listing.square_feet + ' sq ft' : 'N/A'
          }</span>
        </div>
        <a href="/listing/${listing.ml_num}/${listing.address
          .replace(/\s+/g, '-')
          .replace(/[^a-zA-Z0-9\-]/g, '')}" class="popup-view-button">
          View Listing
        </a>
      </div>
    `;
    
    return L.popup({
      closeButton: true,
      className: 'custom-popup',
      maxWidth: 320,
      minWidth: 300
    }).setContent(popupContent);
  };

  // --- Handle Filter Input Changes ---
  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [field]: value, currentPage: 1 };
      
      // Update URL with all filters
      const queryParams = new URLSearchParams({
        ...Object.entries(newFilters).reduce((acc, [key, val]) => {
          if (key === 'propertyTypes' && Array.isArray(val) && val.length > 0) {
            // Convert property types to their codes for the URL
            const selectedCodes = val
              .flatMap((type) => PROPERTY_INFO_CODES[type] || [])
              .join(',');
            acc['property_info'] = selectedCodes;
          } else if (val !== null && val !== undefined && val !== '' && key !== 'currentPage') {
            acc[key] = val;
          }
          return acc;
        }, {}),
        page: 1
      }).toString();
      
      window.history.pushState(null, '', `/map?${queryParams}`);
      
      return newFilters;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPropertyTypeOpen && !event.target.closest('.property-type-filter')) {
        setIsPropertyTypeOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPropertyTypeOpen]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const propertyInfoFromUrl = queryParams.get('property_info');
    
    if (propertyInfoFromUrl) {
      const codesList = propertyInfoFromUrl.split(',');
      const matchedTypes = [];
      
      codesList.forEach(code => {
        if (PROPERTY_INFO_CODES.House.includes(code) && !matchedTypes.includes('House')) {
          matchedTypes.push('House');
        } else if (PROPERTY_INFO_CODES.Condo.includes(code) && !matchedTypes.includes('Condo')) {
          matchedTypes.push('Condo');
        } else if (PROPERTY_INFO_CODES.Townhouse.includes(code) && !matchedTypes.includes('Townhouse')) {
          matchedTypes.push('Townhouse');
        }
      });
      
      if (matchedTypes.length > 0) {
        setSelectedPropertyTypes(matchedTypes);
        setTempPropertyTypes(matchedTypes);
      }
    }
  }, []);

  // --- Handle City Suggestions ---
  const handleCityInputChange = (e) => {
    const input = e.target.value;
    setManualSearchQuery(input);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setFilteredCities([]);
    if (input.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const suggestions = [];
          const cleanedInput = input.trim().toUpperCase();
          const isMLSFormat = /^[A-Z]\d{2,}$/.test(cleanedInput);
          if (isMLSFormat) {
            const mlsResponse = await axios.get('/listings', {
              params: { ml_num: cleanedInput, limit: 5 }
            });
            const mlsSuggestions = mlsResponse.data.map((listing) => {
              const baseAddress = `${listing.address}${
                listing.apt_num ? ` #${listing.apt_num}` : ''
              }`;
              return {
                label: `${baseAddress}, ${listing.municipality}`,
                type: 'MLS',
                listingId: listing.ml_num
              };
            });
            suggestions.push(...mlsSuggestions);
          }
          if (suggestions.length < 5) {
            const matchedCities = uniqueCities
              .filter((city) => city.toLowerCase().startsWith(input.toLowerCase()))
              .map((city) => ({
                label: `${city}, Ontario`,
                type: 'City'
              }));
            suggestions.push(...matchedCities);
          }
          setFilteredCities(suggestions.slice(0, 5));
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      }, 300);
    }
  };

  const handleCitySelection = (selection) => {
    const selectedItem = selection.label.split(',')[0];
    if (selection.type === 'City') {
      handleFilterChange('city', selectedItem);
      setManualSearchQuery('');
    } else if (selection.type === 'MLS' && selection.listingId) {
      const addressSlug = selectedItem
        .replace(/ /g, '-')
        .replace(/[^a-zA-Z0-9\-]/g, '');
      window.location.href = `/listing/${selection.listingId}/${addressSlug}`;
    }
    setFilteredCities([]);
  };

  // --- Render the Filter Bar ---
  const renderFilterBar = () => (
    <div className="filter-bar">
      <div className="filter-item price-range-filter">
        <label>Price Range:</label>
        <div className="price-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.minPrice}
            onChange={(e) =>
              handleFilterChange('minPrice', parseInt(e.target.value, 10) || 0)
            }
            className="price-input"
          />
          <span> - </span>
          <input
            type="number"
            placeholder="Max"
            value={filters.maxPrice}
            onChange={(e) =>
              handleFilterChange('maxPrice', parseInt(e.target.value, 10) || 10000000)
            }
            className="price-input"
          />
        </div>
      </div>
      <div className="filter-item">
        <label>City:</label>
        <select
          value={filters.city}
          onChange={(e) => handleFilterChange('city', e.target.value)}
          className="city-select"
        >
          <option value="All">All Cities</option>
          {uniqueCities.map((city, idx) => (
            <option key={idx} value={city}>
              {city}
            </option>
          ))}
        </select>
      </div>

      <div className='filter-item property-type-filter'>
        <label>Property Type</label>
        <div className="dropdown-container">
          <button 
            className="dropdown-toggle" 
            onClick={(e) => {
              e.stopPropagation();
              setIsPropertyTypeOpen(!isPropertyTypeOpen);
              // Initialize temp state with current selection when opening dropdown
              setTempPropertyTypes(selectedPropertyTypes);
            }}
          >
            {selectedPropertyTypes.length === 0 ? 'Select Type' :
             selectedPropertyTypes.length === 3 ? 'All Types' :
             selectedPropertyTypes.join(', ')}
            <span style={{ float: 'right' }}>▼</span>
          </button>
          
          {isPropertyTypeOpen && (
            <div className="dropdown-menu">
              {/* House Checkbox */}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={tempPropertyTypes.includes('House')}
                  onChange={() => {
                    setTempPropertyTypes((prev) =>
                      prev.includes('House')
                        ? prev.filter((type) => type !== 'House')
                        : [...prev, 'House']
                    );
                  }}
                />
                House
              </label>
            
              {/* Condo Checkbox */}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={tempPropertyTypes.includes('Condo')}
                  onChange={() => {
                    setTempPropertyTypes((prev) =>
                      prev.includes('Condo')
                        ? prev.filter((type) => type !== 'Condo')
                        : [...prev, 'Condo']
                    );
                  }}
                />
                Condo
              </label>
            
              {/* Townhouse Checkbox */}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={tempPropertyTypes.includes('Townhouse')}
                  onChange={() => {
                    setTempPropertyTypes((prev) =>
                      prev.includes('Townhouse')
                        ? prev.filter((type) => type !== 'Townhouse')
                        : [...prev, 'Townhouse']
                    );
                  }}
                />
                Townhouse
              </label>
            
              <button 
                className="apply-button"
                onClick={() => {
                  setSelectedPropertyTypes(tempPropertyTypes);
                  setIsPropertyTypeOpen(false);
                  
                  // Update filter state and fetch new listings
                  handleFilterChange('propertyTypes', tempPropertyTypes);
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="filter-item">
        <label>Bedrooms:</label>
        <select
          value={filters.bedrooms}
          onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
          className="bedrooms-select"
        >
          <option value="Any">Any</option>
          {Array.from({ length: 9 }, (_, idx) => (
            <option key={idx} value={idx + 1}>
              {idx + 1}
            </option>
          ))}
        </select>
      </div>
      <div className="filter-item">
        <label>Bathrooms:</label>
        <select
          value={filters.bathrooms}
          onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
          className="bathrooms-select"
        >
          <option value="Any">Any</option>
          {Array.from({ length: 10 }, (_, idx) => (
            <option key={idx} value={idx + 1}>
              {idx + 1}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // --- Render the Component ---
  return (
    <div className="map-search-wrapper">
      {/* Desktop filter bar */}
      {renderFilterBar()}
      
      {/* Mobile filter section OUTSIDE the filter bar */}
      <div className='mobile-filter-section'>
        <button className='filter-toggle-btn' onClick={toggleMobileFilters}>
          Filter Your Search →
        </button>
        {showMobileFilters && (
          <div className='mobile-filter-options'>
            <div className='filter-item price-range-filter'>
              <label>Price Range:</label>
              <div className="price-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) =>
                    handleFilterChange('minPrice', parseInt(e.target.value, 10) || 0)
                  }
                  className="price-input"
                />
                <span> - </span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) =>
                    handleFilterChange('maxPrice', parseInt(e.target.value, 10) || 10000000)
                  }
                  className="price-input"
                />
              </div>
            </div>
            
            <div className='filter-item'>
              <label>City:</label>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className='city-select'
              >
                <option value='All'>Select City</option>
                {uniqueCities.map((city, idx) => (
                  <option key={idx} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className='filter-item property-type-filter'>
              <label>Property Type</label>
              <div className="dropdown-container">
                <button 
                  className="dropdown-toggle" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPropertyTypeOpen(!isPropertyTypeOpen);
                    setTempPropertyTypes(selectedPropertyTypes);
                  }}
                >
                  {selectedPropertyTypes.length === 0 ? 'Select Type' :
                   selectedPropertyTypes.length === 3 ? 'All Types' :
                   selectedPropertyTypes.join(', ')}
                  <span style={{ float: 'right' }}>▼</span>
                </button>
                
                {isPropertyTypeOpen && (
                  <div className="dropdown-menu">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={tempPropertyTypes.includes('House')}
                        onChange={() => {
                          setTempPropertyTypes((prev) =>
                            prev.includes('House')
                              ? prev.filter((type) => type !== 'House')
                              : [...prev, 'House']
                          );
                        }}
                      />
                      House
                    </label>
                  
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={tempPropertyTypes.includes('Condo')}
                        onChange={() => {
                          setTempPropertyTypes((prev) =>
                            prev.includes('Condo')
                              ? prev.filter((type) => type !== 'Condo')
                              : [...prev, 'Condo']
                          );
                        }}
                      />
                      Condo
                    </label>
                  
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={tempPropertyTypes.includes('Townhouse')}
                        onChange={() => {
                          setTempPropertyTypes((prev) =>
                            prev.includes('Townhouse')
                              ? prev.filter((type) => type !== 'Townhouse')
                              : [...prev, 'Townhouse']
                          );
                        }}
                      />
                      Townhouse
                    </label>
                  
                    <button 
                      className="apply-button"
                      onClick={() => {
                        setSelectedPropertyTypes(tempPropertyTypes);
                        setIsPropertyTypeOpen(false);
                        handleFilterChange('propertyTypes', tempPropertyTypes);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className='filter-item'>
              <label>Bedrooms:</label>
              <select
                value={filters.bedrooms}
                onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                className='bedrooms-select'
              >
                <option value='Any'>Any</option>
                {Array.from({ length: 9 }, (_, idx) => (
                  <option key={idx} value={idx + 1}>
                    {idx + 1}
                  </option>
                ))}
              </select>
            </div>

            <div className='filter-item'>
              <label>Bathrooms:</label>
              <select
                value={filters.bathrooms}
                onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                className='bathrooms-select'
              >
                <option value='Any'>Any</option>
                {Array.from({ length: 10 }, (_, idx) => (
                  <option key={idx} value={idx + 1}>
                    {idx + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      
      {/* Applied filters bar */}
      <div className='applied-filters-bar'>
        {/* Filter buttons */}
      </div>
      
      <div id="map" className="map-container"></div>
      {loading && (
        <div className="map-loading-overlay">
          <div className="map-loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default MapSearch;